import { Injectable } from '@angular/core';
import { AutomataContextService, CurrentAutomationService } from '@galaxy/automata/shared';
import { AutomataService, Choices, EntityType, RunType, WorkflowStep } from '@vendasta/automata';
import moment from 'moment-timezone';
import { catchError, map, skipWhile, switchMap, take } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../common/constants';
import { TranslateService } from '@ngx-translate/core';
import { ManualCompanyTrigger, ManualContactTrigger } from '../automation-editor/component-loader/constants';
import { NoRules } from '../automation-editor/components/triggers/common';
import { Observable, throwError } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AutomationsSideDrawerService } from '../automation-editor/side-drawer/automations-side-drawer-service';

@Injectable({ providedIn: 'root' })
export class EphemeralAutomationService {
  constructor(
    private readonly sideMenuService: AutomationsSideDrawerService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly automationContextService: AutomataContextService,
    private readonly automataService: AutomataService,
    private readonly translateService: TranslateService,
    private readonly snackbarService: SnackbarService,
  ) {}

  public createEphemeralAutomation(namespace: string, entityType: EntityType): Observable<string> {
    let createdAutomationId = '';
    return this.automataService
      .createAutomation({
        namespace: namespace,
        name: 'Bulk action - ' + new Date().toString(),
        entityType: entityType,
        context: this.automationContextService.currentContext,
        settings: {
          runType: RunType.RUN_TYPE_MULTIPLE,
          timeZone: getTimeZone(),
        },
        triggerStep: new WorkflowStep({
          id: TRIGGER_STEP_ID,
          taskDefinitionId: getEphemeralAutomationTriggerTaskDefinitionId(entityType),
          data: '{}',
          choices: new Choices({ rules: NoRules() }),
          name: this.translateService.instant('AUTOMATIONS.COMMON.TRIGGER'),
        }),
        isEphemeralAutomation: true,
        triggerDefinitionId: '/app/automata/manual/crm-object-trigger',
        triggerSubDefinitionId: getEphemeralAutomationEventTriggerSubDefinition(entityType),
      })
      .pipe(
        take(1),
        // We need to set the current automation because the task definition listing uses it. Ideally we'd be able to pass that in,
        // but it's like 20 layers down right now. Setting the current automation does a get on it, so we are skipping doing that
        // and instead are relying on the current automation service so we don't have 2 gets fired off at the same time.
        switchMap((automationId) => {
          createdAutomationId = automationId;
          this.currentAutomationService.init(automationId);
          // The automation starts as null :/
          return this.currentAutomationService.automation$.pipe(skipWhile((automation) => !automation));
        }),
        take(1),
        switchMap((automation) => {
          this.sideMenuService.changeSideMenuContent(
            automation!,
            true,
            false,
            '',
            'selectNewStep',
            '',
            '',
            [],
            'NONE',
            '',
          );
          return this.sideMenuService.afterSaved();
        }),
        // TODO: somewhere here we need to turn on the automation still
        map((afterSaved) => {
          if (afterSaved) {
            return createdAutomationId;
          }
          return '';
        }),
        catchError((err) => {
          console.error('Error creating ephemeral automation:', err);
          this.snackbarService.openErrorSnack('AUTOMATIONS.ERRORS.EPHEMERAL_AUTOMATIONS.STARTING');
          return throwError(err);
        }),
      );
  }
}

function getTimeZone(): string {
  return moment.tz.guess();
}

function getEphemeralAutomationTriggerTaskDefinitionId(entityType: EntityType): string {
  if (entityType === EntityType.ENTITY_TYPE_COMPANY) {
    return ManualCompanyTrigger;
  } else if (entityType === EntityType.ENTITY_TYPE_CONTACT) {
    return ManualContactTrigger;
  }
  return '';
}

function getEphemeralAutomationEventTriggerSubDefinition(entityType: EntityType): string {
  if (entityType === EntityType.ENTITY_TYPE_COMPANY) {
    return 'Company';
  } else if (entityType === EntityType.ENTITY_TYPE_CONTACT) {
    return 'Contact';
  }
  return '';
}
